import { Link } from 'react-router-dom';
import type { KpiCounterTotals } from '../../kpi/hooks.js';
import type { ScenarioRow } from '../../scenario/types.js';
import { formatNumber } from '../utils.js';

/**
 * "Scenarios By Value" table — left-hand widget on the Daily
 * Dashboard. Mirrors the legacy `sec_125` 4-column table:
 *
 *   ┌──────────┬───────┬───────┬────────────┐
 *   │ Scenario │ Hours │ Value │ Operations │
 *   ├──────────┼───────┼───────┼────────────┤
 *   │  name    │ bar+# │ $bar  │  bar+#     │
 *   └──────────┴───────┴───────┴────────────┘
 *
 * Hours / Value come off the per-(scenario, mainMetric) rollup
 * (`rollup`/`mainMetricId` props). When `mainMetricId` is `null`
 * (no main metric set on the license) the Hours / Value columns
 * render `—` placeholders. Operations comes off the rich
 * `useScenariosList()` row already.
 *
 * Visual contract matches the legacy widget's bar-tint cells:
 *   - Hours      : violet
 *   - Value      : brown / orange
 *   - Operations : blue
 */
export interface ScenariosByValueTableProps {
  rows: ReadonlyArray<ScenarioRow>;
  rollup: Map<string, Map<string, KpiCounterTotals>>;
  mainMetricId: string | null;
}

export function ScenariosByValueTable({
  rows,
  rollup,
  mainMetricId,
}: ScenariosByValueTableProps): JSX.Element {
  const maxOps = rows.reduce((m, r) => {
    const ops = Number(r.lastSuccessfulRunOperations ?? 0);
    return Number.isFinite(ops) && ops > m ? ops : m;
  }, 0);
  // Resolve the (hours, value) for one scenario. When the license's
  // `mainMetricId` is set + present in the rollup, use it; otherwise
  // fall back to summing across all metrics so the table shows
  // something useful in demo mode (where the fixture metric ids
  // don't match a real license's `mainMetricId`) and in licenses
  // that haven't picked a main metric yet.
  function totalsFor(fusionId: string): { hours: number; value: number } | null {
    const byMetric = rollup.get(fusionId);
    if (!byMetric) return null;
    if (mainMetricId && byMetric.has(mainMetricId)) {
      const t = byMetric.get(mainMetricId)!;
      return { hours: t.quantity, value: t.value };
    }
    let hours = 0;
    let value = 0;
    for (const t of byMetric.values()) {
      hours += t.quantity;
      value += t.value;
    }
    return hours === 0 && value === 0 ? null : { hours, value };
  }
  const { maxHours, maxValue } = rows.reduce(
    (acc, r) => {
      if (!r.fusionId) return acc;
      const totals = totalsFor(r.fusionId);
      if (!totals) return acc;
      return {
        maxHours: Math.max(acc.maxHours, totals.hours),
        maxValue: Math.max(acc.maxValue, totals.value),
      };
    },
    { maxHours: 0, maxValue: 0 },
  );
  const totals = rows.reduce(
    (acc, r) => {
      const t = r.fusionId ? totalsFor(r.fusionId) : null;
      const ops = Number(r.lastSuccessfulRunOperations ?? 0);
      return {
        hours: acc.hours + (t?.hours ?? 0),
        value: acc.value + (t?.value ?? 0),
        operations: acc.operations + (Number.isFinite(ops) ? ops : 0),
      };
    },
    { hours: 0, value: 0, operations: 0 },
  );

  return (
    <section className="overflow-hidden rounded-lg border border-foreground/10 bg-foreground/[0.02]">
      <header className="border-b border-foreground/10 px-3 py-2 text-sm font-semibold text-foreground">
        Scenarios By Value
      </header>
      {rows.length === 0 ? (
        <p className="px-3 py-6 text-center text-sm text-foreground/50">No scenarios available.</p>
      ) : (
        <div className="max-h-[420px] overflow-y-auto">
          <table className="w-full table-fixed text-sm">
            <thead className="text-xs uppercase tracking-wider text-foreground/55">
              <tr className="border-b border-foreground/10">
                <th className="w-[55%] px-3 py-2 text-left font-medium">Scenario</th>
                <th
                  className="px-2 py-2 text-right font-medium text-foreground"
                  style={{ backgroundColor: 'rgba(168, 85, 247, 0.18)' }}
                >
                  Hours
                </th>
                <th
                  className="px-2 py-2 text-right font-medium text-foreground"
                  style={{ backgroundColor: 'rgba(180, 83, 9, 0.18)' }}
                >
                  Value
                </th>
                <th
                  className="px-2 py-2 text-right font-medium text-foreground"
                  style={{ backgroundColor: 'rgba(59, 130, 246, 0.18)' }}
                >
                  Operations
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const ops = Number(r.lastSuccessfulRunOperations ?? 0);
                const opsPct = maxOps > 0 ? Math.min(100, (ops / maxOps) * 100) : 0;
                const totals = r.fusionId ? totalsFor(r.fusionId) : null;
                const hours = totals?.hours ?? 0;
                const value = totals?.value ?? 0;
                const hoursPct = maxHours > 0 ? Math.min(100, (hours / maxHours) * 100) : 0;
                const valuePct = maxValue > 0 ? Math.min(100, (value / maxValue) * 100) : 0;
                return (
                  <tr
                    key={r.fusionId}
                    className="border-b border-foreground/5 align-middle last:border-b-0"
                  >
                    <td className="truncate px-3 py-2 text-foreground" title={r.name}>
                      <Link
                        to={`/scenarios?scenarioId=${encodeURIComponent(r.fusionId)}`}
                        className="text-foreground hover:underline"
                      >
                        {r.name}
                      </Link>
                    </td>
                    {totals ? (
                      <BarCell
                        value={hours}
                        formatted={formatNumber(hours)}
                        pct={hoursPct}
                        tint="rgb(168, 85, 247)"
                      />
                    ) : (
                      <td className="px-2 py-2 text-right font-mono text-foreground/40">—</td>
                    )}
                    {totals ? (
                      <BarCell
                        value={value}
                        formatted={`$${formatNumber(value)}`}
                        pct={valuePct}
                        tint="rgb(180, 83, 9)"
                      />
                    ) : (
                      <td className="px-2 py-2 text-right font-mono text-foreground/40">—</td>
                    )}
                    <BarCell
                      value={Number.isFinite(ops) ? ops : 0}
                      formatted={formatNumber(Number.isFinite(ops) ? ops : 0)}
                      pct={opsPct}
                      tint="rgb(59, 130, 246)"
                    />
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-foreground/[0.04] text-sm">
              <tr className="border-t border-foreground/15">
                <td className="px-3 py-2 text-left font-semibold text-foreground">Total</td>
                <td
                  className="px-2 py-2 text-right font-mono font-semibold text-foreground"
                  style={{ backgroundColor: 'rgba(168, 85, 247, 0.18)' }}
                >
                  {formatNumber(totals.hours)}
                </td>
                <td
                  className="px-2 py-2 text-right font-mono font-semibold text-foreground"
                  style={{ backgroundColor: 'rgba(180, 83, 9, 0.18)' }}
                >
                  ${formatNumber(totals.value)}
                </td>
                <td
                  className="px-2 py-2 text-right font-mono font-semibold text-foreground"
                  style={{ backgroundColor: 'rgba(59, 130, 246, 0.18)' }}
                >
                  {formatNumber(totals.operations)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}

function BarCell({
  formatted,
  pct,
  tint,
}: {
  value: number;
  formatted: string;
  pct: number;
  tint: string;
}): JSX.Element {
  return (
    <td className="relative px-2 py-2 text-right text-foreground">
      <div className="absolute inset-0">
        <div
          className="h-full"
          style={{ width: `${pct}%`, backgroundColor: tint, opacity: 0.25 }}
        />
      </div>
      <span className="relative font-mono">{formatted}</span>
    </td>
  );
}
